import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Grid,
  Card,
  CardMedia,
  Stack,
  alpha,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Modal } from '../../../components/BootstrapToMUI';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';

export default function ProductPositionModal(props) {
  const theme = useTheme();
  const { brand, setBrand, handleSubmit, referTo, ...rest } = props;
  const [localBrand, setLocalBrand] = useState([]);

  // Initialize local state when modal opens
  useEffect(() => {
    if (rest.show && brand) {
      setLocalBrand([...brand]);
    }
  }, [rest.show, brand]);

  const moveUp = (index) => {
    if (index === 0) return;
    const newBrand = [...localBrand];
    [newBrand[index], newBrand[index - 1]] = [newBrand[index - 1], newBrand[index]];
    setLocalBrand(newBrand);
    setBrand(newBrand);
  };

  const moveDown = (index) => {
    if (index === localBrand.length - 1) return;
    const newBrand = [...localBrand];
    [newBrand[index], newBrand[index + 1]] = [newBrand[index + 1], newBrand[index]];
    setLocalBrand(newBrand);
    setBrand(newBrand);
  };

  const currentBrand = (localBrand.length > 0 ? localBrand : brand) || [];

  return (
    <Modal className="modal-w" {...rest} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          Arrange Your Products Position
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container maxWidth="lg">
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 3,
              color: theme.palette.text.secondary,
              fontWeight: 500,
            }}
          >
            Use arrows to reorder products - changes reflect in real-time on the right
          </Typography>

          <Grid container spacing={2}>
            {/* Left side - Product list with arrows */}
            <Grid item xs={12} md={5}>
              <Paper
                elevation={2}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.05
                  )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  maxHeight: '650px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1.5, color: theme.palette.primary.main, fontSize: '0.9rem' }}
                >
                  Product Order
                </Typography>
                <Stack spacing={0.8} sx={{ overflowY: 'auto', flex: 1, pr: 0.5 }}>
                  {currentBrand.map((prod, index) => (
                    <Card
                      key={prod._id}
                      elevation={0}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 0.8,
                        borderRadius: 1.5,
                        transition: 'all 0.2s ease',
                        border: `1.5px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        '&:hover': {
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          transform: 'translateX(3px)',
                        },
                      }}
                    >
                      {/* Position number */}
                      <Box
                        sx={{
                          minWidth: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          mr: 1,
                        }}
                      >
                        {index + 1}
                      </Box>

                      {/* Drag indicator */}
                      <DragIndicatorIcon
                        sx={{ color: theme.palette.text.disabled, mr: 0.5, fontSize: '1rem' }}
                      />

                      {/* Product name */}
                      <Typography
                        variant="body2"
                        sx={{ flex: 1, fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.3 }}
                      >
                        {prod?.id?.name || 'Unknown'}
                      </Typography>

                      {/* Arrow buttons */}
                      <Stack direction="row" spacing={0.3}>
                        <IconButton
                          size="small"
                          onClick={() => moveUp(index)}
                          disabled={index === 0}
                          sx={{
                            padding: '4px',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.2),
                            },
                            '&:disabled': {
                              backgroundColor: alpha(theme.palette.action.disabled, 0.05),
                            },
                          }}
                        >
                          <ArrowUpwardIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveDown(index)}
                          disabled={index === currentBrand.length - 1}
                          sx={{
                            padding: '4px',
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.main, 0.2),
                            },
                            '&:disabled': {
                              backgroundColor: alpha(theme.palette.action.disabled, 0.05),
                            },
                          }}
                        >
                          <ArrowDownwardIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Stack>
                    </Card>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            {/* Right side - Horizontal scrolling product preview */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: alpha(theme.palette.background.paper, 0.8),
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 2, color: theme.palette.primary.main, fontSize: '0.9rem' }}
                >
                  Preview
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    pb: 2,
                    '&::-webkit-scrollbar': {
                      height: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                      background: alpha(theme.palette.grey[300], 0.3),
                      borderRadius: 4,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: alpha(theme.palette.secondary.main, 0.5),
                      borderRadius: 4,
                      '&:hover': {
                        background: alpha(theme.palette.secondary.main, 0.7),
                      },
                    },
                  }}
                >
                  {currentBrand.map((prod, index) => {
                    const picture = prod?.id?.productPictures?.[0] || '';
                    const url = picture ? `${process.env.REACT_APP_SERVER_API}/pictures/${picture}/${referTo}` : '';
                    return (
                      <Box
                        key={prod._id}
                        sx={{
                          minWidth: 160,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        {/* Product card */}
                        <Card
                          elevation={1}
                          sx={{
                            width: '100%',
                            borderRadius: 2,
                            border: `2px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
                              transform: 'translateY(-4px)',
                              borderColor: alpha(theme.palette.secondary.main, 0.5),
                            },
                          }}
                        >
                          {/* Product image */}
                          <Box
                            sx={{
                              height: 160,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 2,
                              backgroundColor: alpha(theme.palette.grey[100], 0.3),
                            }}
                          >
                            <CardMedia
                              component="img"
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                              }}
                              image={url}
                              alt={prod?.id?.name || 'Product'}
                            />
                          </Box>

                          {/* Product name */}
                          <Box
                            sx={{
                              p: 1.5,
                              borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                              minHeight: 60,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                textAlign: 'center',
                                lineHeight: 1.3,
                                fontSize: '0.85rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {prod?.id?.name || 'Unknown'}
                            </Typography>
                          </Box>
                        </Card>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Button
            fullWidth
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
              },
              transition: 'all 0.3s ease',
            }}
          >
            Save Product Order
          </Button>
        </Container>
      </Modal.Body>
    </Modal>
  );
}
